import React,{useCallback,useEffect,useMemo,useState}from"react";
import{Box,Button,CircularProgress,Dialog,DialogActions,DialogContent,DialogTitle,Paper,Stack,Typography}from"@mui/material";
import SendRoundedIcon from"@mui/icons-material/SendRounded";
import DownloadRoundedIcon from"@mui/icons-material/DownloadRounded";
import CheckCircleRoundedIcon from"@mui/icons-material/CheckCircleRounded";
import WarningAmberRoundedIcon from"@mui/icons-material/WarningAmberRounded";
import ErrorOutlineRoundedIcon from"@mui/icons-material/ErrorOutlineRounded";
import{useLocation}from"react-router-dom";
import Sidebar from"./AdminPanel/Sidebar";
import TopBar from"./AdminPanel/TopBar";
import DetailInvoice from"./AdminInvoice/DetailInvoice";
import DetailProduct from"./AdminInvoice/DetailProduct";
import PrintInvoice from"./AdminInvoice/PrintInvoice";
import{useDashboardTheme}from"../../context/DashboardThemeContext";
import{useAuth}from"../../context/AuthContext";
import{useAlert}from"../../context/AlertContext";

const CASH_DRAWER_ENDPOINT="http://127.0.0.1:5050/open-cash-drawer";
const PERSON_ENDPOINTS={CLIENTE:"/api/facturacion/facturas/clientes/search/",PROVEEDOR:"/api/facturacion/facturas/proveedores/search/"};

function getToken(auth){
  return auth?.accessToken||auth?.access||localStorage.getItem("accessToken")||localStorage.getItem("access")||localStorage.getItem("token")||"";
}

function getSearchParam(search,key){
  return new URLSearchParams(search).get(key)||"";
}

function round2(value){
  return Math.round((Number(value||0)+Number.EPSILON)*100)/100;
}

function round6(value){
  return Math.round((Number(value||0)+Number.EPSILON)*1000000)/1000000;
}

function normalizeIva(value){
  const iva=Number(value||0);
  if(iva===999)return 999;
  if([0,5,15].includes(iva))return iva;
  return 0;
}

function hasValue(value){
  return value!==null&&value!==undefined&&value!=="";
}

function pickNumber(...values){
  for(const value of values){
    if(hasValue(value))return Number(value||0);
  }
  return 0;
}

function getFinalFromBase(base,ivaPorcentaje){
  const precioBase=Number(base||0);
  const iva=normalizeIva(ivaPorcentaje);
  if(iva===999||iva<=0)return round6(precioBase);
  return round6(precioBase+precioBase*(iva/100));
}

function calculateLineFromBase(precioSinIva,ivaPorcentaje,cantidad){
  const baseUnitario=Number(precioSinIva||0);
  const iva=normalizeIva(ivaPorcentaje);
  const qty=Number(cantidad||0);
  const subtotal=round6(baseUnitario*qty);
  if(iva===999||iva<=0)return{precioSinIva:round6(baseUnitario),precioConIva:round6(baseUnitario),ivaValor:0,subtotal,total:subtotal};
  const ivaValor=round6(subtotal*(iva/100));
  const total=round6(subtotal+ivaValor);
  return{precioSinIva:round6(baseUnitario),precioConIva:getFinalFromBase(baseUnitario,iva),ivaValor,subtotal,total};
}

function cleanText(value){
  return String(value||"").trim();
}

function buildResumenFromDetalles(detalles=[]){
  const data={subtotalSinIva:0,baseIva0:0,baseIva5:0,baseIva15:0,baseNoObjeto:0,iva5:0,iva15:0,ivaTotal:0,total:0};
  detalles.forEach(detalle=>{
    const subtotal=pickNumber(detalle.subtotal,detalle.total_sin_iva,detalle.base_imponible);
    const ivaValor=pickNumber(detalle.iva_valor,detalle.iva_total);
    const total=pickNumber(detalle.total,round6(subtotal+ivaValor));
    const iva=normalizeIva(detalle.iva_porcentaje);
    data.subtotalSinIva+=subtotal;
    data.ivaTotal+=ivaValor;
    data.total+=total;
    if(iva===5){
      data.baseIva5+=subtotal;
      data.iva5+=ivaValor;
    }else if(iva===15){
      data.baseIva15+=subtotal;
      data.iva15+=ivaValor;
    }else if(iva===999){
      data.baseNoObjeto+=subtotal;
    }else{
      data.baseIva0+=subtotal;
    }
  });
  data.subtotalSinIva=round2(data.subtotalSinIva);
  data.baseIva0=round2(data.baseIva0);
  data.baseIva5=round2(data.baseIva5);
  data.baseIva15=round2(data.baseIva15);
  data.baseNoObjeto=round2(data.baseNoObjeto);
  data.iva5=round2(data.iva5);
  data.iva15=round2(data.iva15);
  data.ivaTotal=round2(data.ivaTotal);
  data.total=round2(data.total);
  return data;
}

function normalizeDetalleFromOrden(detalle){
  const cantidad=pickNumber(detalle.cantidad);
  const ivaPorcentaje=normalizeIva(detalle.iva_porcentaje);
  const precioSinIva=pickNumber(detalle.precio_unitario,detalle.precio_sin_iva);
  const calculo=calculateLineFromBase(precioSinIva,ivaPorcentaje,cantidad);
  const subtotal=hasValue(detalle.subtotal)?round6(detalle.subtotal):hasValue(detalle.total_sin_iva)?round6(detalle.total_sin_iva):hasValue(detalle.base_imponible)?round6(detalle.base_imponible):calculo.subtotal;
  const ivaValor=hasValue(detalle.iva_valor)?round6(detalle.iva_valor):hasValue(detalle.iva_total)?round6(detalle.iva_total):calculo.ivaValor;
  const total=hasValue(detalle.total)?round6(detalle.total):round6(subtotal+ivaValor);
  const nombre=cleanText(detalle.nombre||detalle.producto_nombre||"");
  const descripcionOriginal=cleanText(detalle.descripcion||"");
  const descripcion=descripcionOriginal&&descripcionOriginal!==nombre?descripcionOriginal:"";
  return{uid:`orden-${detalle.id||Date.now()}-${Math.random().toString(16).slice(2)}`,backend_id:detalle.id||"",producto_id:detalle.producto_id||detalle.producto||detalle.producto_id_ref||"",codigo_principal:detalle.codigo_principal||detalle.codigo||detalle.producto_codigo||"",codigo_auxiliar:detalle.codigo_auxiliar||detalle.producto_codigo_auxiliar||"",nombre,descripcion,cantidad:round6(cantidad),precio_unitario:round6(precioSinIva),precio_sin_iva:round6(precioSinIva),precio_con_iva:calculo.precioConIva,iva_porcentaje:ivaPorcentaje,iva_valor:ivaValor,subtotal,total,observacion:detalle.observacion||""};
}

function expandPersonDocuments(personas=[]){
  return personas.map(persona=>{
    if(persona.ruc)return{...persona,option_uid:`${persona.id}-RUC`,cedula:"",ruc:persona.ruc,documento_tipo:"RUC",documento_valor:persona.ruc};
    if(persona.cedula)return{...persona,option_uid:`${persona.id}-CEDULA`,cedula:persona.cedula,ruc:"",documento_tipo:"CEDULA",documento_valor:persona.cedula};
    return{...persona,option_uid:`${persona.id}-SIN`,cedula:"",ruc:"",documento_tipo:"",documento_valor:""};
  });
}

function getFacturaFromData(data){
  return data?.factura||data?.factura_actual||data?.orden?.factura||null;
}

function getSriMessage(data){
  const sri=data?.sri||null;
  if(!sri)return"";
  if(sri.ok===false||sri.debe_reintentar)return"El SRI presenta fallas o dejó la factura pendiente. No generes nuevamente la factura. Revisa el estado del documento antes de intentar otra acción.";
  return"";
}

async function sendCashDrawerPulse(){
  try{
    const response=await fetch(CASH_DRAWER_ENDPOINT,{method:"POST"});
    if(!response.ok)return false;
    const data=await response.json().catch(()=>null);
    return Boolean(data?.ok);
  }catch{
    return false;
  }
}

export default function AdminInvoice(){
  const[sidebarOpen,setSidebarOpen]=useState(true);
  const{dashboardTheme}=useDashboardTheme();
  const{showError,showSuccess}=useAlert();
  const auth=useAuth();
  const accessToken=getToken(auth);
  const location=useLocation();
  const ordenId=useMemo(()=>getSearchParam(location.search,"orden"),[location.search]);
  const[loading,setLoading]=useState(true);
  const[personLoading,setPersonLoading]=useState(false);
  const[saving,setSaving]=useState(false);
  const[downloadingPdf,setDownloadingPdf]=useState(false);
  const[formData,setFormData]=useState(null);
  const[orden,setOrden]=useState(location.state?.orden||null);
  const[detallesFactura,setDetallesFactura]=useState([]);
  const[personOptions,setPersonOptions]=useState([]);
  const[personInput,setPersonInput]=useState("");
  const[createdFactura,setCreatedFactura]=useState(null);
  const[modalOpen,setModalOpen]=useState(false);
  const[modalType,setModalType]=useState("success");
  const[modalTitle,setModalTitle]=useState("");
  const[modalMessage,setModalMessage]=useState("");
  const[invoiceData,setInvoiceData]=useState({
    tipo_persona:"CLIENTE",
    persona:null,
    documento_tipo:"",
    documento_valor:"",
    fecha_emision:"",
    establecimiento:"",
    punto_emision_id:"",
    descripcion:"",
    pagos:[{forma_pago:"EFECTIVO",monto:"",transferencia_codigo:""}],
    valor_recibido:"",
    emitir:true,
    generar_xml:true
  });

  const showModal=useCallback((type,title,message)=>{
    setModalType(type);
    setModalTitle(title);
    setModalMessage(message);
    setModalOpen(true);
  },[]);

  const authFetch=useCallback(async(url,options={})=>{
    const headers={Accept:"application/json",...(options.headers||{})};
    if(accessToken)headers.Authorization=`Bearer ${accessToken}`;
    return fetch(url,{...options,headers});
  },[accessToken]);

  const parseJson=async response=>{
    try{
      return await response.json();
    }catch{
      return{ok:false,error:"Respuesta inválida del servidor"};
    }
  };

  const updateInvoice=patch=>{
    setInvoiceData(prev=>({...prev,...patch}));
  };

  const resumenFactura=useMemo(()=>buildResumenFromDetalles(detallesFactura),[detallesFactura]);

  const loadFormData=useCallback(async()=>{
    setLoading(true);
    try{
      const query=ordenId?`?orden_id=${encodeURIComponent(ordenId)}`:"";
      const response=await authFetch(`/api/facturacion/facturas/form-data/${query}`);
      const data=await parseJson(response);
      if(!response.ok||!data.ok){
        showModal("error","No se pudo cargar el formulario","No se pudo cargar la información para facturar.");
        setFormData(null);
        return;
      }
      const orderData=data.orden||location.state?.orden||null;
      const defaultPunto=data.puntos?.[0]||null;
      const defaultEstablecimiento=defaultPunto?.establecimiento||data.establecimientos?.[0]?.codigo||"";
      const defaultFecha=data.fecha_emision||new Date().toISOString().slice(0,10);
      const backendDetalles=orderData?.detalles||[];
      const facturaExistente=getFacturaFromData(data)||getFacturaFromData(orderData);
      const normalizedDetalles=backendDetalles.map(normalizeDetalleFromOrden);
      const resumenInicial=buildResumenFromDetalles(normalizedDetalles);
      const totalInicial=round2(resumenInicial.total||orderData?.total||0);
      setFormData(data);
      setOrden(orderData);
      setPersonOptions(expandPersonDocuments(data.clientes||[]));
      if(facturaExistente?.id)setCreatedFactura(facturaExistente);
      setDetallesFactura(prev=>prev.length?prev:normalizedDetalles);
      setInvoiceData(prev=>({
        ...prev,
        fecha_emision:prev.fecha_emision||defaultFecha,
        establecimiento:prev.establecimiento||defaultEstablecimiento,
        punto_emision_id:prev.punto_emision_id||defaultPunto?.id||"",
        descripcion:prev.descripcion||data.descripcion||(orderData?.numero?`Orden #${orderData.numero}`:""),
        pagos:prev.pagos?.length?prev.pagos.map((pago,index)=>index===0&&(!pago.monto||Number(pago.monto)===0)?{...pago,monto:String(totalInicial)}:pago):[{forma_pago:"EFECTIVO",monto:String(totalInicial),transferencia_codigo:""}],
        valor_recibido:prev.valor_recibido||String(totalInicial)
      }));
    }catch{
      showModal("error","No se pudo cargar el formulario","No se pudo cargar la información para facturar.");
      setFormData(null);
    }finally{
      setLoading(false);
    }
  },[authFetch,ordenId,location.state,showModal]);

  const searchPersons=useCallback(async(query="")=>{
    const endpoint=PERSON_ENDPOINTS[invoiceData.tipo_persona];
    if(!endpoint)return;
    setPersonLoading(true);
    try{
      const response=await authFetch(`${endpoint}?q=${encodeURIComponent(query)}`);
      const data=await parseJson(response);
      if(response.ok&&data.ok){
        const list=invoiceData.tipo_persona==="CLIENTE"?data.clientes||[]:data.proveedores||[];
        setPersonOptions(expandPersonDocuments(list));
      }else{
        setPersonOptions([]);
      }
    }catch{
      setPersonOptions([]);
    }finally{
      setPersonLoading(false);
    }
  },[authFetch,invoiceData.tipo_persona]);

  useEffect(()=>{
    loadFormData();
  },[loadFormData]);

  useEffect(()=>{
    const timer=setTimeout(()=>searchPersons(personInput),350);
    return()=>clearTimeout(timer);
  },[personInput,searchPersons]);

  useEffect(()=>{
    if(invoiceData.tipo_persona==="CLIENTE"){
      setPersonOptions(expandPersonDocuments(formData?.clientes||[]));
    }else{
      setPersonOptions([]);
      searchPersons("");
    }
  },[invoiceData.tipo_persona,formData,searchPersons]);

  useEffect(()=>{
    if(!resumenFactura.total)return;
    setInvoiceData(prev=>{
      const pagos=prev.pagos||[];
      if(pagos.length!==1)return prev;
      const pago=pagos[0];
      const montoActual=Number(pago.monto||0);
      const totalAnterior=Number(prev._ultimo_total_pago||0);
      if(montoActual!==0&&totalAnterior!==0&&round2(montoActual)!==round2(totalAnterior))return{...prev,_ultimo_total_pago:resumenFactura.total};
      return{
        ...prev,
        pagos:[{...pago,monto:String(resumenFactura.total)}],
        valor_recibido:pago.forma_pago==="EFECTIVO"?(prev.valor_recibido||String(resumenFactura.total)):String(resumenFactura.total),
        _ultimo_total_pago:resumenFactura.total
      };
    });
  },[resumenFactura.total]);

  const establecimientos=formData?.establecimientos||[];
  const puntos=formData?.puntos||[];
  const formasPago=formData?.formas_pago||[];

  const availablePuntos=useMemo(()=>{
    if(!invoiceData.establecimiento)return puntos;
    return puntos.filter(item=>String(item.establecimiento)===String(invoiceData.establecimiento));
  },[puntos,invoiceData.establecimiento]);

  const selectedPunto=useMemo(()=>puntos.find(item=>String(item.id)===String(invoiceData.punto_emision_id))||null,[puntos,invoiceData.punto_emision_id]);

  const documentoFactura=useMemo(()=>{
    if(createdFactura?.numero)return createdFactura.numero;
    if(selectedPunto?.proximo_numero)return selectedPunto.proximo_numero;
    if(formData?.proximo_numero)return formData.proximo_numero;
    return"";
  },[selectedPunto,formData,createdFactura]);

  useEffect(()=>{
    if(!availablePuntos.length)return;
    const exists=availablePuntos.some(item=>String(item.id)===String(invoiceData.punto_emision_id));
    if(!exists)updateInvoice({punto_emision_id:availablePuntos[0].id});
  },[availablePuntos,invoiceData.punto_emision_id]);

  const pagosValidos=useMemo(()=>{
    const pagos=invoiceData.pagos||[];
    if(!pagos.length||pagos.length>2)return false;
    if(pagos.some(pago=>!pago.forma_pago||Number(pago.monto||0)<=0))return false;
    if(pagos.length===2&&pagos[0].forma_pago===pagos[1].forma_pago)return false;
    const totalPagos=round2(pagos.reduce((sum,pago)=>sum+Number(pago.monto||0),0));
    return totalPagos===round2(resumenFactura.total);
  },[invoiceData.pagos,resumenFactura.total]);

  const canSubmit=useMemo(()=>{
    if(createdFactura?.id||orden?.facturada||!invoiceData.fecha_emision||!invoiceData.punto_emision_id||!invoiceData.persona||!invoiceData.documento_valor||!detallesFactura.length||!pagosValidos)return false;
    return true;
  },[invoiceData,detallesFactura,createdFactura,orden,pagosValidos]);

  const canDownloadPdf=Boolean(createdFactura?.id);

  const addDetalleFactura=detalle=>{
    setDetallesFactura(prev=>[...prev,detalle]);
  };

  const updateDetalleFactura=(uid,patch)=>{
    setDetallesFactura(prev=>prev.map(detalle=>{
      if(detalle.uid!==uid)return detalle;
      const merged={...detalle,...patch};
      const calculo=calculateLineFromBase(merged.precio_sin_iva||merged.precio_unitario,merged.iva_porcentaje,merged.cantidad);
      return{...merged,cantidad:round6(merged.cantidad),precio_unitario:calculo.precioSinIva,precio_sin_iva:calculo.precioSinIva,precio_con_iva:calculo.precioConIva,iva_valor:calculo.ivaValor,subtotal:calculo.subtotal,total:calculo.total};
    }));
  };

  const removeDetalleFactura=uid=>{
    setDetallesFactura(prev=>prev.filter(detalle=>detalle.uid!==uid));
  };

  const downloadFacturaPdf=async()=>{
    if(!createdFactura?.id)return;
    setDownloadingPdf(true);
    try{
      const response=await authFetch(`/api/facturacion/facturas/${createdFactura.id}/pdf/`,{method:"GET",headers:{Accept:"application/pdf"}});
      if(!response.ok){
        showError("El PDF todavía no está disponible.");
        return;
      }
      const blob=await response.blob();
      const url=window.URL.createObjectURL(blob);
      const a=document.createElement("a");
      const numero=createdFactura.numero||createdFactura.id;
      a.href=url;
      a.download=`Factura_${numero}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showSuccess("PDF descargado correctamente.");
    }catch{
      showError("No se pudo descargar el PDF.");
    }finally{
      setDownloadingPdf(false);
    }
  };

  const submitInvoice=async()=>{
    if(!canSubmit){
      showError("Completa los datos obligatorios y verifica que los pagos sumen exactamente el total.");
      return;
    }
    setSaving(true);
    const pagos=(invoiceData.pagos||[]).map(pago=>({
      forma_pago:pago.forma_pago,
      monto:String(round2(pago.monto)),
      transferencia_codigo:pago.forma_pago==="TRANSFERENCIA"?pago.transferencia_codigo||"":""
    }));
    const tieneEfectivo=pagos.some(pago=>pago.forma_pago==="EFECTIVO");
    let cashDrawerOpened=false;
    const cashDrawerPromise=tieneEfectivo?sendCashDrawerPulse().then(ok=>{
      cashDrawerOpened=ok;
      return ok;
    }):Promise.resolve(false);
    const persona=invoiceData.persona;
    const primerPago=pagos[0]||{};
    const payload={
      orden_id:orden?.id||"",
      tipo_persona:invoiceData.tipo_persona,
      persona_id:persona?.id||"",
      cliente_id:invoiceData.tipo_persona==="CLIENTE"?persona?.id||"":"",
      proveedor_id:invoiceData.tipo_persona==="PROVEEDOR"?persona?.id||"":"",
      fecha_emision:invoiceData.fecha_emision,
      punto_emision_id:invoiceData.punto_emision_id,
      establecimiento:selectedPunto?.establecimiento||invoiceData.establecimiento,
      descripcion:invoiceData.descripcion||(orden?.numero?`Orden #${orden.numero}`:""),
      descripcion_factura:invoiceData.descripcion||(orden?.numero?`Orden #${orden.numero}`:""),
      comprador_tipo_documento:invoiceData.documento_tipo,
      tipo_documento:invoiceData.documento_tipo,
      comprador_identificacion:invoiceData.documento_valor,
      comprador_razon_social:persona?.razon_social||"CONSUMIDOR FINAL",
      comprador_direccion:persona?.direccion||"",
      comprador_correo:persona?.correo||"",
      comprador_telefono:persona?.telefono||"",
      pagos,
      forma_pago:primerPago.forma_pago||"EFECTIVO",
      valor_recibido:pagos.length===1&&primerPago.forma_pago==="EFECTIVO"?invoiceData.valor_recibido||primerPago.monto:primerPago.monto||"0",
      transferencia_codigo:primerPago.forma_pago==="TRANSFERENCIA"?primerPago.transferencia_codigo||"":"",
      emitir:invoiceData.emitir?"1":"0",
      generar_xml:invoiceData.generar_xml?"1":"0",
      detalles:detallesFactura.map(detalle=>({
        producto_id:detalle.producto_id||"",
        codigo_principal:detalle.codigo_principal||"",
        codigo_auxiliar:detalle.codigo_auxiliar||"",
        descripcion:detalle.descripcion||"",
        producto_nombre:detalle.nombre||"",
        cantidad:String(round6(detalle.cantidad||0)),
        precio_unitario:String(round6(detalle.precio_sin_iva||detalle.precio_unitario||0)),
        iva_porcentaje:String(detalle.iva_porcentaje||0),
        subtotal:String(round6(detalle.subtotal||0)),
        iva_valor:String(round6(detalle.iva_valor||0)),
        total:String(round6(detalle.total||0)),
        observacion:detalle.observacion||""
      }))
    };
    try{
      const response=await authFetch("/api/facturacion/facturas/create-from-order/",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(payload)});
      const data=await parseJson(response);
      const factura=data.factura||null;
      cashDrawerOpened=await cashDrawerPromise;
      if(factura?.id){
        setCreatedFactura(factura);
        setOrden(prev=>prev?{...prev,facturada:true,factura}:prev);
      }
      if(!response.ok||!data.ok){
        if(factura?.id){
          showModal("warning","Factura enviada",`${getSriMessage(data)||"El SRI presenta fallas o dejó la factura pendiente. No generes nuevamente la factura. Revisa el estado del documento antes de intentar otra acción."}${cashDrawerOpened?" Caja abierta correctamente.":""}`);
          return;
        }
        showError(data.error||data.detail||"No se pudo completar la factura.");
        return;
      }
      const sriWarning=getSriMessage(data);
      if(sriWarning){
        showModal("warning","Factura enviada al SRI",`${sriWarning}${cashDrawerOpened?" Caja abierta correctamente.":""}`);
      }else{
        showModal("success","Factura creada correctamente",`${data.message||"Factura creada correctamente."}${cashDrawerOpened?" Caja abierta correctamente.":""}`);
      }
    }catch{
      cashDrawerOpened=await cashDrawerPromise;
      showError(`No se pudo completar el proceso. Revisa el estado en Documentos antes de intentar nuevamente.${cashDrawerOpened?" Caja abierta correctamente.":""}`);
    }finally{
      setSaving(false);
    }
  };

  const modalIcon=useMemo(()=>{
    if(modalType==="success")return<CheckCircleRoundedIcon sx={{fontSize:44,color:"#16a34a"}}/>;
    if(modalType==="warning")return<WarningAmberRoundedIcon sx={{fontSize:44,color:"#f59e0b"}}/>;
    return<ErrorOutlineRoundedIcon sx={{fontSize:44,color:"#ef4444"}}/>;
  },[modalType]);

  const outlinedButtonSx={minHeight:42,borderRadius:"10px",textTransform:"none",fontWeight:700,borderColor:dashboardTheme.button_color,color:dashboardTheme.button_color,px:3,boxShadow:"none","&:hover":{borderColor:dashboardTheme.button_hover_color,color:dashboardTheme.button_hover_color,boxShadow:"none"}};

  return(
    <Box sx={{minHeight:"100vh",bgcolor:dashboardTheme.dashboard_background,display:"flex",color:dashboardTheme.text_color,overflowX:"hidden"}}>
      <Sidebar open={sidebarOpen} onClose={()=>setSidebarOpen(false)}/>
      <Box sx={{flex:1,minWidth:0,minHeight:"100vh",display:"flex",flexDirection:"column"}}>
        <TopBar sidebarOpen={sidebarOpen} onToggleSidebar={()=>setSidebarOpen(prev=>!prev)}/>
        <Box component="main" sx={{flex:1,p:{xs:1.5,sm:2,md:3}}}>
          <Paper elevation={0} sx={{borderRadius:"14px",border:`1px solid ${dashboardTheme.border_color}`,bgcolor:dashboardTheme.card_background,p:{xs:1.5,md:2},boxShadow:"none"}}>
            {loading?(
              <Box sx={{minHeight:360,display:"flex",alignItems:"center",justifyContent:"center"}}><CircularProgress/></Box>
            ):(
              <Box>
                <DetailInvoice orden={orden} invoiceData={invoiceData} updateInvoice={updateInvoice} personOptions={personOptions} personLoading={personLoading} personInput={personInput} setPersonInput={setPersonInput} establecimientos={establecimientos} availablePuntos={availablePuntos} formasPago={formasPago} documentoFactura={documentoFactura} resumenFactura={resumenFactura} onPersonCreated={persona=>{
                  const expanded=expandPersonDocuments([persona]);
                  const selected=expanded[0]||persona;
                  setPersonOptions(prev=>[selected,...prev.filter(item=>String(item.id)!==String(persona.id))]);
                  updateInvoice({persona:selected,documento_tipo:selected.documento_tipo||"",documento_valor:selected.documento_valor||""});
                }}/>
                <DetailProduct detalles={detallesFactura} resumen={resumenFactura} onAddDetail={addDetalleFactura} onUpdateDetail={updateDetalleFactura} onRemoveDetail={removeDetalleFactura} bloqueado={Boolean(createdFactura?.id||orden?.facturada)}/>
                <Box sx={{mt:2,display:"flex",justifyContent:"flex-end",gap:1.2,flexWrap:"wrap"}}>
                  {createdFactura?.id&&<PrintInvoice factura={createdFactura} authFetch={authFetch} disabled={downloadingPdf}/>}
                  {canDownloadPdf&&(
                    <Button variant="outlined" startIcon={downloadingPdf?<CircularProgress size={18}/>:<DownloadRoundedIcon/>} disabled={downloadingPdf} onClick={downloadFacturaPdf} sx={outlinedButtonSx}>
                      Descargar PDF
                    </Button>
                  )}
                  {!createdFactura?.id&&!orden?.facturada&&(
                    <Button variant="contained" startIcon={saving?<CircularProgress size={18}/>:<SendRoundedIcon/>} disabled={!canSubmit||saving} onClick={submitInvoice} sx={{minHeight:42,borderRadius:"10px",textTransform:"none",fontWeight:700,bgcolor:dashboardTheme.button_color,color:dashboardTheme.button_text_color,px:3,boxShadow:"none","&:hover":{bgcolor:dashboardTheme.button_hover_color,boxShadow:"none"},"&.Mui-disabled":{opacity:.55,color:dashboardTheme.button_text_color}}}>
                      Emitir factura
                    </Button>
                  )}
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
      <Dialog open={modalOpen} onClose={()=>setModalOpen(false)} maxWidth="xs" fullWidth PaperProps={{sx:{borderRadius:"14px",bgcolor:dashboardTheme.card_background,color:dashboardTheme.text_color,border:`1px solid ${dashboardTheme.border_color}`,boxShadow:"none"}}}>
        <DialogTitle sx={{pb:0}}>
          <Stack spacing={1.2} alignItems="center" textAlign="center">
            {modalIcon}
            <Typography sx={{fontWeight:700,fontSize:18}}>{modalTitle}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{pt:2}}>
          <Typography sx={{fontSize:14,textAlign:"center",color:dashboardTheme.text_color}}>{modalMessage}</Typography>
        </DialogContent>
        <DialogActions sx={{px:2.5,pb:2.5,justifyContent:"center",gap:1,flexWrap:"wrap"}}>
          <Button variant="contained" onClick={()=>setModalOpen(false)} sx={{borderRadius:"10px",textTransform:"none",fontWeight:700,bgcolor:dashboardTheme.button_color,color:dashboardTheme.button_text_color,boxShadow:"none","&:hover":{bgcolor:dashboardTheme.button_hover_color,boxShadow:"none"}}}>Aceptar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}